import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import cn from "classnames";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Briefcase, ArrowLeft, ListChecks, FolderOpen, UserCheck, GripVertical, Plus, Trash2, Pencil, Lock, Calendar, MessageSquare, UserPlus } from "lucide-react";
import styles from "./DraftEditor.module.sass";
import { getAuthToken } from "../../utils/authHelpers";
import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import DraftPreview from "../../components/DraftPreview";
import TemplateManager from "../../components/TemplateManager";
import { useChatContext } from "../../contexts/ChatContext";
import { displayHandle } from "../../utils/streamHelpers";
import { getCloudinaryUrl, getOptimizedAvatarUrl } from "../../utils/cloudinary";

const DEFAULT_AVATAR = "/images/content/avatar-1.jpg";

const DEFAULT_EXPECTATIONS = [
  { title: "Flexible Collaboration Dates" },
  { title: "Specific Collaboration Dates (see notes)" },
  { title: "Hosted 2-night Stay" },
  { title: "Paid Partnership" },
  { title: "Travel Compensation" },
  { title: "Professional Photography Equipment" },
  { title: "Drone Footage" },
  { title: "Listing Photos" },
  { title: "Lifestyle Photos" },
  { title: "B-Roll video/reel" },
  { title: "In-feed Reel" },
  { title: "In-feed Carousel Post" },
  { title: "3-5 Stories per day" },
  { title: "Giveaway" },
  { title: "Creator must tag host when ads are shot on property" },
  { title: "Creator to Provide Contract" },
  { title: "Host to Provide Contract" },
  { title: "No Contract Necessary" },
];

const STEPS = [
  { id: "agreements", step: 1, label: "Expectations + Deliverables", mobileLabel: "Expectations", Icon: ListChecks },
  { id: "media", step: 2, label: "Content Library", mobileLabel: "Content", Icon: FolderOpen },
  { id: "review", step: 3, label: "Review", mobileLabel: "Review", Icon: UserCheck },
];

const apiBase = () =>
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "";

const SortableExpectation = ({ id, title, onChangeTitle, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const textareaRef = useRef(null);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(styles.agreementRow, { [styles.dragging]: isDragging })}
    >
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        <GripVertical size={16} />
      </div>
      <div className={styles.agreementText}>
        <textarea
          ref={textareaRef}
          rows={1}
          value={title}
          onChange={(e) => onChangeTitle(e.target.value)}
          placeholder="e.g. 3-night hosted stay, 2 Instagram Reels, 1 blog post..."
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = e.target.scrollHeight + "px";
          }}
        />
      </div>
      <div className={styles.rowActions}>
        <button className={styles.editBtn} onClick={() => textareaRef.current?.focus()}>
          <Pencil size={14} />
        </button>
        <button className={styles.deleteBtn} onClick={onDelete}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const DraftEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("id");

  const { searchUsers, isConnected, initializeChat } = useChatContext();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  const [title, setTitle] = useState("");
  const [recipientUserId, setRecipientUserId] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [targetKind, setTargetKind] = useState(null);
  const [targetId, setTargetId] = useState(null);

  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [expectations, setExpectations] = useState([]);
  const [notes, setNotes] = useState("");

  // Keep these for backwards compat with terms JSONB
  const [compensationType, setCompensationType] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [primaryGoal, setPrimaryGoal] = useState("");
  const [guestCapacity, setGuestCapacity] = useState("");

  const [activeTab, setActiveTab] = useState("agreements");
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showRecipientSearch, setShowRecipientSearch] = useState(false);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipientResults, setRecipientResults] = useState([]);
  const [recipientSearching, setRecipientSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);

  // Current user info
  const [currentUser, setCurrentUser] = useState(null);

  // Host-side property selection: which of the host's listed properties this
  // draft is about. Stored on the draft as targetKind='property' + targetId.
  const [myProperties, setMyProperties] = useState([]);
  const [showPropertyPicker, setShowPropertyPicker] = useState(false);

  const saveTimerRef = useRef(null);
  const isNewRef = useRef(!draftId);

  const userType = typeof window !== "undefined"
    ? window.localStorage.getItem("postaway_user_type") || "host"
    : "host";
  const isHost = userType === "host";
  const isCreator = userType === "creator";
  const otherRole = isHost ? "Creator" : "Host";

  // Load current user profile for the hero card
  useEffect(() => {
    const email = typeof window !== "undefined" ? window.localStorage.getItem("postaway_email") || "" : "";
    if (!email) return;

    const endpoint = isHost ? "/api/host/profile" : "/api/creator/profile";
    fetch(`${apiBase()}${endpoint}?email=${encodeURIComponent(email)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.ok && data.profile) {
          const p = data.profile;
          setCurrentUser({
            firstName: p.first_name || p.brand_name || p.owner_name || email.split("@")[0],
            handle: p.handle || null,
            avatarUrl: p.avatar_url || p.banner_url || p.logo_url || null,
            heroImageUrl: p.hero_image_url || p.banner_url || p.avatar_url || null,
          });
        }
        if (data.ok && isHost && Array.isArray(data.properties)) {
          setMyProperties(data.properties);
        }
      })
      .catch(() => {});
  }, [isHost]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const buildTerms = useCallback(() => ({
    dates: { start: dateStart || null, end: dateEnd || null },
    expectations: expectations.filter((e) => e.title.trim()),
    deliverables: expectations.filter((e) => e.title.trim()),
    compensation: {
      type: compensationType || null,
      ...(compensationType === "paid" ? { budgetMin: budgetMin || null, budgetMax: budgetMax || null } : {}),
    },
    goals: { primaryGoal: primaryGoal || null },
    notes: notes || null,
    guestCapacity: guestCapacity || null,
  }), [dateStart, dateEnd, expectations, compensationType, budgetMin, budgetMax, primaryGoal, notes, guestCapacity]);

  const populateFromTerms = (terms) => {
    if (!terms) return;
    if (terms.dates) {
      setDateStart(terms.dates.start || "");
      setDateEnd(terms.dates.end || "");
    }
    const expList = terms.expectations || terms.deliverables || [];
    if (Array.isArray(expList) && expList.length > 0) {
      setExpectations(expList.map((e, i) => ({
        id: `exp-${i}-${Date.now()}`,
        title: e.title || "",
        description: e.description || "",
      })));
    }
    if (terms.compensation) {
      setCompensationType(terms.compensation.type || "");
      setBudgetMin(terms.compensation.budgetMin || "");
      setBudgetMax(terms.compensation.budgetMax || "");
    }
    if (terms.goals?.primaryGoal) setPrimaryGoal(terms.goals.primaryGoal);
    if (terms.notes) setNotes(terms.notes);
    if (terms.guestCapacity) setGuestCapacity(String(terms.guestCapacity));
  };

  // Load existing draft or seed defaults for new
  useEffect(() => {
    if (!draftId) {
      setExpectations(DEFAULT_EXPECTATIONS.map((e, i) => ({
        id: `exp-${i}-${Date.now()}`,
        title: e.title,
        description: "",
      })));
      setLoading(false);
      return;
    }
    const token = getAuthToken();
    if (!token) { setError("Please log in"); setLoading(false); return; }

    (async () => {
      try {
        const res = await fetch(`${apiBase()}/api/collab/drafts/get?draftId=${draftId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.ok) { setError(data.error || "Failed to load draft"); return; }
        const d = data.draft;
        setTitle(d.title || "");
        setRecipientUserId(d.recipientUserId || null);
        setRecipient(d.recipient || null);
        setTargetKind(d.targetKind || null);
        setTargetId(d.targetId || null);
        populateFromTerms(d.terms);
        // Drafts created with a recipient pre-attached (Explore New Collab ->
        // "Start Draft") are saved server-side with empty terms. Seed the same
        // standard checklist a blank draft gets so both paths start equal.
        const savedExpectations = d.terms?.expectations || d.terms?.deliverables || [];
        if (!Array.isArray(savedExpectations) || savedExpectations.length === 0) {
          setExpectations(DEFAULT_EXPECTATIONS.map((e, i) => ({
            id: `exp-${i}-${Date.now()}`,
            title: e.title,
            description: "",
          })));
        }
        isNewRef.current = false;
      } catch (err) {
        console.error("Error loading draft:", err);
        setError("Failed to load draft");
      } finally {
        setLoading(false);
      }
    })();
  }, [draftId]);

  // Auto-save
  const saveDraft = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    setSaving(true);
    try {
      const terms = buildTerms();
      if (isNewRef.current) {
        const res = await fetch(`${apiBase()}/api/collab/drafts/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ recipientUserId, targetKind, targetId, title, terms }),
        });
        const data = await res.json();
        if (data.ok) {
          isNewRef.current = false;
          window.history.replaceState({}, "", `/collabs/draft?id=${data.draftId}`);
        }
      } else {
        const currentId = draftId || new URLSearchParams(window.location.search).get("id");
        if (!currentId) return;
        await fetch(`${apiBase()}/api/collab/drafts/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ draftId: currentId, recipientUserId, targetKind, targetId, title, terms }),
        });
      }
      setLastSaved(new Date());
    } catch (err) {
      console.error("Auto-save error:", err);
    } finally {
      setSaving(false);
    }
  }, [draftId, recipientUserId, targetKind, targetId, title, buildTerms]);

  useEffect(() => {
    if (loading) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveDraft(), 1500);
    return () => clearTimeout(saveTimerRef.current);
  }, [title, recipientUserId, targetKind, targetId, dateStart, dateEnd, expectations, compensationType, budgetMin, budgetMax, primaryGoal, notes, guestCapacity, loading, saveDraft]);

  // Recipient search
  useEffect(() => {
    if (!showRecipientSearch) return;
    if (!isConnected) {
      const token = getAuthToken();
      if (token) initializeChat(token).catch(() => {});
    }
  }, [showRecipientSearch, isConnected, initializeChat]);

  useEffect(() => {
    if (!showRecipientSearch || !isConnected) return;
    const timer = setTimeout(async () => {
      setRecipientSearching(true);
      try {
        const res = await searchUsers(recipientQuery, { includeProperties: true });
        setRecipientResults(res || []);
      } catch { setRecipientResults([]); }
      finally { setRecipientSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [recipientQuery, showRecipientSearch, isConnected, searchUsers]);

  const getRecipientUserId = (user) => {
    if (user.userId) return String(user.userId);
    if (user.id) {
      const parts = user.id.split("-");
      if (parts.length > 1 && ["creator", "host", "admin", "user", "traveler", "property"].includes(parts[0])) {
        return parts.slice(1).join("-");
      }
      return user.id;
    }
    return null;
  };

  const handleSelectRecipient = (user) => {
    const uid = getRecipientUserId(user);
    setRecipientUserId(uid);
    const isProp = user.resultType === "property";
    setRecipient({
      userId: uid,
      firstName: user.first_name || user.name?.split(" ")[0] || (isProp ? user.propertyName : null),
      handle: user.handle || user.username || (isProp ? user.propertySlug : null),
      avatarUrl: user.image || null,
    });
    if (isProp && user.propertyId) {
      setTargetKind("property");
      setTargetId(user.propertyId);
      if (!title && user.propertyName) setTitle(user.propertyName);
    }
    setShowRecipientSearch(false);
    setRecipientQuery("");
  };

  // Expectations helpers
  const addExpectation = () => {
    setExpectations((prev) => [...prev, { id: `exp-${Date.now()}`, title: "", description: "" }]);
  };
  const updateExpectationTitle = (id, newTitle) => {
    setExpectations((prev) => prev.map((e) => (e.id === id ? { ...e, title: newTitle } : e)));
  };
  const deleteExpectation = (id) => {
    setExpectations((prev) => prev.filter((e) => e.id !== id));
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setExpectations((prev) => {
        const oldIndex = prev.findIndex((e) => e.id === active.id);
        const newIndex = prev.findIndex((e) => e.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleSelectProperty = (prop) => {
    setTargetKind("property");
    setTargetId(prop.id);
    if (!title && prop.name) setTitle(prop.name);
    setShowPropertyPicker(false);
  };

  const handleUseTemplate = (template) => {
    populateFromTerms(template.terms);
    if (template.terms?.title) setTitle(template.terms.title);
    setShowTemplateManager(false);
  };

  const handleSend = () => {
    if (!recipientUserId || sending) return;
    setShowNameModal(true);
  };

  const handleConfirmSend = async () => {
    if (!recipientUserId || sending) return;
    setShowNameModal(false);
    setSending(true);
    try {
      await saveDraft();
      const currentId = draftId || new URLSearchParams(window.location.search).get("id");
      if (!currentId) { alert("Please save the draft first"); setSending(false); return; }
      const token = getAuthToken();
      const res = await fetch(`${apiBase()}/api/collab/drafts/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ draftId: currentId }),
      });
      const data = await res.json();
      if (data.ok) navigate("/collabs");
      else alert(data.error || "Failed to send");
    } catch (err) {
      console.error("Send error:", err);
      alert("Failed to send proposal");
    } finally { setSending(false); }
  };

  // Hero avatar helpers
  const getHeroSrc = (url) => {
    if (!url) return DEFAULT_AVATAR;
    return getCloudinaryUrl(url, { width: 450, height: 600, crop: "fill", quality: "auto", format: "auto" }) || url;
  };

  const getSearchAvatarSrc = (url) => {
    if (!url) return null;
    return getOptimizedAvatarUrl(url) || url;
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn("section", styles.section)}>
        <div className={styles.container}>
          <div className={styles.workspace}>
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span>Loading workspace...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("section", styles.section)}>
        <div className={styles.container}>
          <div className={styles.workspace}>
            <div className={styles.error}>
              <h3>Could not load draft</h3>
              <p>{error}</p>
              <button className={styles.sendBtn} onClick={() => navigate("/collabs")}>
                Back to Collabs
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Host-side property selection (which listing this draft is about)
  const selectedProperty = isHost && targetKind === "property" && targetId
    ? myProperties.find((p) => String(p.id) === String(targetId)) || null
    : null;

  // Build hero cards
  const myHeroCard = currentUser ? (
    <div className={styles.heroCard}>
      <div className={styles.heroAvatarWrap}>
        <img
          src={getHeroSrc(selectedProperty?.main_image_url || currentUser.heroImageUrl || currentUser.avatarUrl)}
          alt={selectedProperty?.name || currentUser.firstName}
          className={styles.heroAvatar}
        />
      </div>
      <div className={styles.heroCardInfo}>
        <span className={styles.heroName}>{currentUser.firstName}</span>
        {selectedProperty ? (
          <span className={styles.heroHandle}>
            {selectedProperty.slug ? `@${selectedProperty.slug}` : selectedProperty.name}
          </span>
        ) : (
          currentUser.handle && <span className={styles.heroHandle}>@{currentUser.handle}</span>
        )}
        <span className={cn(styles.heroRole, isHost ? styles.heroRoleHost : styles.heroRoleCreator)}>
          {isHost ? "Host" : "Creator"}
        </span>
        {isHost && myProperties.length > 0 && (
          <button
            onClick={() => setShowPropertyPicker(true)}
            style={{ marginTop: 4, background: "none", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
          >
            {selectedProperty ? "Change property" : "Select property"}
          </button>
        )}
      </div>
    </div>
  ) : (
    <div className={styles.heroCard}>
      <div className={styles.heroAvatarWrap}>
        <img src={DEFAULT_AVATAR} alt="You" className={styles.heroAvatar} />
      </div>
      <div className={styles.heroCardInfo}>
        <span className={styles.heroName}>You</span>
        <span className={cn(styles.heroRole, isHost ? styles.heroRoleHost : styles.heroRoleCreator)}>
          {isHost ? "Host" : "Creator"}
        </span>
      </div>
    </div>
  );

  const otherHeroCard = recipient ? (
    <div className={styles.heroCard}>
      <div className={styles.heroAvatarWrap}>
        <img
          src={getHeroSrc(recipient.avatarUrl)}
          alt={recipient.firstName || otherRole}
          className={styles.heroAvatar}
        />
      </div>
      <div className={styles.heroCardInfo}>
        <span className={styles.heroName}>{recipient.firstName || otherRole}</span>
        {recipient.handle && <span className={styles.heroHandle}>@{recipient.handle}</span>}
        <span className={cn(styles.heroRole, isHost ? styles.heroRoleCreator : styles.heroRoleHost)}>
          {otherRole}
        </span>
        <button
          onClick={() => setShowRecipientSearch(true)}
          style={{ marginTop: 4, background: "none", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
        >
          Change
        </button>
      </div>
    </div>
  ) : (
    <div className={styles.heroPlaceholder} onClick={() => setShowRecipientSearch(true)}>
      <div className={styles.heroPlaceholderBox}>
        <UserPlus size={32} />
        <span className={styles.heroPlaceholderText}>Pick your {otherRole.toLowerCase()}</span>
      </div>
    </div>
  );

  return (
    <>
      <div className={cn("section", styles.section)}>
        <div className={styles.container}>
          <div className={styles.workspace}>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerRow}>
                <div className={styles.headerLeft}>
                  <button className={styles.backBtn} onClick={() => navigate("/collabs")} aria-label="Go back">
                    <ArrowLeft size={20} />
                  </button>
                  <Briefcase size={20} style={{ stroke: "#0f084b", fill: "none" }} />
                  <h2>{title || "Draft Collaboration"}</h2>
                  <span className={cn(styles.statusChip, styles.statusDraft)}>draft</span>
                </div>
                {lastSaved && (
                  <span className={styles.savedIndicator}>
                    {saving ? "Saving..." : `Saved ${lastSaved.toLocaleTimeString()}`}
                  </span>
                )}
              </div>

              {/* Hero Row */}
              <div className={styles.heroRow}>
                {isHost ? myHeroCard : otherHeroCard}
                <span className={styles.heroCross}>&times;</span>
                {isHost ? otherHeroCard : myHeroCard}
              </div>

              <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.5, margin: '0 0 4px', padding: '0 4px' }}>
                Build your collab proposal. Outline what you're looking for, set expectations, and send it to your collaborator when you're ready. Nothing is sent until you hit "Send Proposal."
              </p>
            </div>

            {/* Step Tracker */}
            <div className={styles.stepTracker}>
              <div className={styles.stepNav} role="tablist">
                {STEPS.map((step) => {
                  const isActive = activeTab === step.id;
                  const isDisabled = step.id !== "agreements";
                  return (
                    <button
                      key={step.id}
                      role="tab"
                      aria-selected={isActive}
                      className={cn(styles.stepItem, {
                        [styles.stepActive]: isActive,
                        [styles.stepDisabled]: isDisabled,
                      })}
                      onClick={() => {
                        if (!isDisabled) setActiveTab(step.id);
                      }}
                      title={isDisabled ? "Available after sending" : step.label}
                    >
                      <span className={styles.stepMarker}>
                        {isDisabled ? <Lock size={14} /> : step.step}
                      </span>
                      <span className={styles.stepLabel}>
                        <step.Icon size={14} className={styles.stepLabelIcon} />
                        <span className={styles.stepLabelFull}>{step.label}</span>
                        <span className={styles.stepLabelMobile}>{step.mobileLabel}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className={styles.content}>
              {activeTab === "agreements" && (
                <>
                  <div className={styles.agreementsTab}>
                    <div className={styles.agreementsDescription}>
                      <p>
                        <strong>What's the plan?</strong> List out everything both sides are agreeing to — from the stay itself to what gets posted and when. Think of each item as a checkbox that both you and your collaborator will confirm later.
                      </p>
                      <p style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                        Drag to reorder, click to edit, or use the icons to make changes. You can always update these before sending.
                      </p>
                    </div>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={expectations.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                        <div className={styles.agreementsList}>
                          {expectations.map((exp) => (
                            <SortableExpectation
                              key={exp.id}
                              id={exp.id}
                              title={exp.title}
                              onChangeTitle={(val) => updateExpectationTitle(exp.id, val)}
                              onDelete={() => deleteExpectation(exp.id)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>

                    <button className={styles.addExpectationBtn} onClick={addExpectation}>
                      <Plus size={16} /> Add Item
                    </button>
                  </div>

                  {/* Approx Dates */}
                  <div className={styles.approxDateSection}>
                    <div className={styles.approxDateHeader}>
                      <Calendar size={16} />
                      <span>Approximate Dates</span>
                    </div>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
                      When is this happening? The collaboration date is when the stay or activity takes place, and the posting date is when content should go live. These are flexible and can be adjusted later.
                    </p>
                    <div className={styles.approxDateGrid}>
                      <div className={styles.approxDateField}>
                        <span>Collaboration Date</span>
                        <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
                      </div>
                      <div className={styles.approxDateField}>
                        <span>Posting Date</span>
                        <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
                      </div>
                    </div>
                  </div>

                  {/* General Notes */}
                  <div className={styles.generalNotesSection}>
                    <div className={styles.generalNotesHeader}>
                      <MessageSquare size={16} />
                      <span>General Notes</span>
                    </div>
                    <textarea
                      className={styles.generalNotesTextarea}
                      placeholder="Anything else to mention? Special requests, timing preferences, content style notes..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </>
              )}

              {activeTab === "media" && (
                <div className={styles.lockedTabContent}>
                  <Lock size={48} />
                  <h3>Content Library</h3>
                  <p>This unlocks after your collaborator accepts. Once you're both in, use the Content Library to share photos, videos, links, and any assets related to the collab.</p>
                </div>
              )}

              {activeTab === "review" && (
                <div className={styles.lockedTabContent}>
                  <Lock size={48} />
                  <h3>Review</h3>
                  <p>This unlocks after deliverables are submitted. When everything's wrapped up, you'll both leave a review and mark the collab as complete.</p>
                </div>
              )}
            </div>

            {/* Action Bar */}
            <div className={styles.actions}>
              <button className={styles.templateBtn} onClick={() => setShowTemplateManager(true)} title="Save this as a reusable template for future collabs">
                <Icon name="flag" size="16" /> Save as Template
              </button>
              <button className={styles.previewBtn} onClick={async () => { await saveDraft(); navigate("/collabs?tab=drafts"); }} title="Save your progress and come back later">
                <Icon name="check-circle" size="16" /> Save Draft
              </button>
              <button
                className={styles.sendBtn}
                onClick={handleSend}
                disabled={!recipientUserId || sending}
                title="Send this collab proposal to your collaborator for review"
              >
                <Icon name="send" size="16" /> {sending ? "Sending..." : "Send Proposal"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recipient Search Modal */}
      {showRecipientSearch && (
        <Modal visible={showRecipientSearch} onClose={() => setShowRecipientSearch(false)}>
          <div className={styles.searchModal}>
            <h3 className={styles.searchTitle}>Select a {otherRole}</h3>
            <input
              className={styles.searchInput}
              type="text"
              autoFocus
              placeholder={`Search ${otherRole.toLowerCase()}s, users, properties...`}
              value={recipientQuery}
              onChange={(e) => setRecipientQuery(e.target.value)}
            />
            <div className={styles.searchResults}>
              {recipientSearching && <p className={styles.searchEmpty}>Searching...</p>}
              {!recipientSearching && recipientResults.length === 0 && (
                <p className={styles.searchEmpty}>No results</p>
              )}
              {!recipientSearching && recipientResults.map((u) => (
                <button key={u.id} className={styles.searchRow} onClick={() => handleSelectRecipient(u)}>
                  {u.image ? (
                    <img src={getSearchAvatarSrc(u.image) || u.image} alt="" className={styles.searchAvatar} />
                  ) : (
                    <div className={styles.searchAvatarPlaceholder}>
                      <UserPlus size={16} />
                    </div>
                  )}
                  <div>
                    <div className={styles.searchName}>
                      {u.handle || u.username ? `@${(u.handle || u.username).replace(/^@/, "")}` : u.name || u.propertyName || "User"}
                    </div>
                    {u.first_name && <div className={styles.searchSub}>{u.first_name}</div>}
                    {u.resultType === "property" && <div className={styles.searchBadge}>Property</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {/* Property Picker Modal (hosts with listed properties) */}
      {showPropertyPicker && (
        <Modal visible={showPropertyPicker} onClose={() => setShowPropertyPicker(false)}>
          <div className={styles.searchModal}>
            <h3 className={styles.searchTitle}>Which property is this collab for?</h3>
            <div className={styles.searchResults}>
              {myProperties.map((prop) => {
                const isSelected = selectedProperty && String(selectedProperty.id) === String(prop.id);
                const loc = [prop.location_city, prop.location_state].filter(Boolean).join(", ");
                return (
                  <button
                    key={prop.id}
                    className={styles.searchRow}
                    onClick={() => handleSelectProperty(prop)}
                    style={isSelected ? { background: "#f1f5f9" } : undefined}
                  >
                    {prop.main_image_url ? (
                      <img
                        src={getCloudinaryUrl(prop.main_image_url, { width: 96, height: 96, crop: "fill", quality: "auto", format: "auto" }) || prop.main_image_url}
                        alt=""
                        className={styles.searchAvatar}
                      />
                    ) : (
                      <div className={styles.searchAvatarPlaceholder}>
                        <UserPlus size={16} />
                      </div>
                    )}
                    <div>
                      <div className={styles.searchName}>
                        {prop.slug ? `@${prop.slug}` : prop.name || "Property"}
                      </div>
                      {loc && <div className={styles.searchSub}>{loc}</div>}
                      {isSelected && <div className={styles.searchBadge}>Selected</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </Modal>
      )}

      {/* Preview */}
      {showPreview && (
        <DraftPreview
          visible={showPreview}
          onClose={() => setShowPreview(false)}
          draft={{ title, recipient, terms: buildTerms() }}
          onSend={handleConfirmSend}
          sending={sending}
          canSend={!!recipientUserId}
        />
      )}

      {/* Name & Send Modal */}
      {showNameModal && (
        <Modal visible={showNameModal} onClose={() => setShowNameModal(false)}>
          <div className={styles.nameModal}>
            <h3 className={styles.nameModalTitle}>Give it a name</h3>
            <p className={styles.nameModalSub}>This helps both of you find and reference the collab later. Pick something descriptive — like the property name, the trip, or the campaign.</p>
            <input
              className={styles.nameModalInput}
              type="text"
              autoFocus
              placeholder="e.g. June Stay at The Grand Colorado"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <div className={styles.nameModalActions}>
              <button className={styles.nameModalCancel} onClick={() => setShowNameModal(false)}>
                Cancel
              </button>
              <button
                className={styles.nameModalSend}
                onClick={handleConfirmSend}
                disabled={sending}
              >
                <Icon name="send" size="16" />
                {sending ? "Sending..." : "Send Proposal"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Template Manager */}
      {showTemplateManager && (
        <TemplateManager
          visible={showTemplateManager}
          onClose={() => setShowTemplateManager(false)}
          currentTerms={buildTerms()}
          currentTitle={title}
          onUseTemplate={handleUseTemplate}
        />
      )}
    </>
  );
};

export default DraftEditor;
